import { findBestTransfer, getDistanceBetweenPropertyAndAirport } from 'components/Experiences/ExperienceTransfers/ExperienceTransferUtils'
import getOrderAccommodationStartDate from 'lib/order/getOrderAccommodationStartDate'
import getOrderAccommodationEndDate from 'lib/order/getOrderAccommodationEndDate'
import type { LoyaltyTransferExperience } from './determineOrderLoyaltyTransferExperience'

const EmptyResult = {
  canUseOneWay: false,
  canUseReturn: false,
  bestToHotelOption: undefined,
  bestToAirportOption: undefined,
}

/**
 * Pure per-order variant of the `getLoyaltyEligibleTransferOptions` selector. Use when the
 * order is not the route-viewed order (e.g. the upcoming-escapes card). Pass the transfer
 * experience from {@link determineOrderLoyaltyTransferExperience} for the same order.
 */
export default function determineLoyaltyEligibleTransferOptions(
  order: App.Order | undefined,
  transferExperience: LoyaltyTransferExperience,
  experienceTransferOptions: App.State['experience']['experienceTransferOptions'],
) {
  if (!order || !transferExperience) return EmptyResult

  const property = order.items[0]?.offer?.property
  if (!property) return EmptyResult

  const startDate = getOrderAccommodationStartDate(order)
  const endDate = getOrderAccommodationEndDate(order)

  // Get one-click transfer options for this experience
  const options = Object.values(experienceTransferOptions)
    .flatMap(opt => opt?.transfers ?? [])
    .filter(item => item.experienceId === transferExperience.id)

  const toHotelOptions = options.filter(t => t.transferTypes.includes('AIRPORT-TO-HOTEL'))
  const toAirportOptions = options.filter(t => t.transferTypes.includes('HOTEL-TO-AIRPORT'))

  // Build default transfer views
  const airport = transferExperience.originalExperience?.airport
  const item = order.items[0]
  const adults = item?.reservation?.adults || 2
  const children = item?.reservation?.children || 0

  const toHotelView: App.ExperienceTransferView = {
    type: 'AIRPORT-TO-HOTEL',
    date: startDate,
    travellers: { adults, children },
    hotel: {
      latitude: property.latitude,
      longitude: property.longitude,
      kmFromAirport: getDistanceBetweenPropertyAndAirport(property as any, airport),
      name: property.name,
    },
    baggage: { bags: 0, oversizedBags: 0 },
  }

  const toAirportView: App.ExperienceTransferView = {
    type: 'HOTEL-TO-AIRPORT',
    date: endDate,
    travellers: { adults, children },
    hotel: {
      latitude: property.latitude,
      longitude: property.longitude,
      kmFromAirport: getDistanceBetweenPropertyAndAirport(property as any, airport),
      name: property.name,
    },
    baggage: { bags: 0, oversizedBags: 0 },
  }

  // Find best transfers
  const { bestTransfer: bestToHotelOption } = findBestTransfer(toHotelView, toHotelOptions)
  const { bestTransfer: bestToAirportOption } = findBestTransfer(
    toAirportView,
    toAirportOptions.filter(opt => opt.isLoyaltyEligible),
  )

  const canUseOneWay = (bestToHotelOption?.loyaltyOptions?.eligibleTransfer === 'ONE-WAY') ||
    (bestToAirportOption?.loyaltyOptions?.eligibleTransfer === 'ONE-WAY')
  const canUseReturn = (bestToHotelOption?.loyaltyOptions?.eligibleTransfer === 'RETURN') ||
    (bestToAirportOption?.loyaltyOptions?.eligibleTransfer === 'RETURN')

  return { canUseOneWay, canUseReturn, bestToHotelOption, bestToAirportOption }
}
